import { AllUserRoleIds } from "@/constants/userRoles";
import { RouteRecordRaw } from "vue-router";

// Shared by the UiShowcase hub and every showcase child — the whole UI kit is a
// public, dev-only reference area.
const uiKitMeta = {
  requiresAuth: false,
  scrollToTop: true,
  allowedRoleIds: AllUserRoleIds,
};

export default [
  // ── UI-kit hub ───────────────────────────────────────────────────────────────
  // UiShowcase is both the index screen (a button per showcase) and the outlet host:
  // it renders a nested <IonRouterOutlet> that the showcase children below push into.
  // Child paths are relative, so every pre-existing /ui-kit/*-showcase URL is unchanged.
  {
    path: "/ui-kit",
    name: "UiShowcase",
    component: () => import(/* webpackChunkName: "UiShowcase" */ "@/views/uiKit/UiShowcase.vue"),
    meta: uiKitMeta,
    children: [
      // Index child (path "") — the hub's landing screen. UiShowcase is a pure shell that
      // renders route.matched[1], so the button grid lives here as its own IonPage. This keeps
      // the route tree in the final Ionic shell shape (shell + nested children).
      {
        path: "",
        component: () =>
          import(/* webpackChunkName: "UiShowcaseIndex" */ "@/views/uiKit/UiShowcaseIndex.vue"),
        name: "UiShowcaseIndex",
        meta: uiKitMeta,
      },
      {
        path: "color-palette-showcase",
        component: () =>
          import(
            /* webpackChunkName: "ColorPaletteShowcase" */ "@/views/uiKit/ColorPaletteShowcase.vue"
          ),
        name: "ColorPaletteShowcase",
        meta: uiKitMeta,
      },
      {
        path: "primitives-showcase",
        component: () =>
          import(
            /* webpackChunkName: "PrimitivesShowcase" */ "@/views/uiKit/PrimitivesShowcase.vue"
          ),
        name: "PrimitivesShowcase",
        meta: uiKitMeta,
      },
      {
        path: "atoms-showcase",
        component: () =>
          import(/* webpackChunkName: "AtomsShowcase" */ "@/views/uiKit/AtomsShowcase.vue"),
        name: "AtomsShowcase",
        meta: uiKitMeta,
      },
      {
        path: "inputs-showcase",
        component: () =>
          import(/* webpackChunkName: "InputsShowcase" */ "@/views/uiKit/InputsShowcase.vue"),
        name: "InputsShowcase",
        meta: uiKitMeta,
      },
      {
        path: "selectors-showcase",
        component: () =>
          import(/* webpackChunkName: "SelectorsShowcase" */ "@/views/uiKit/SelectorsShowcase.vue"),
        name: "SelectorsShowcase",
        meta: uiKitMeta,
      },
      {
        path: "ionic-showcase",
        component: () =>
          import(/* webpackChunkName: "IonicShowcase" */ "@/views/uiKit/IonicShowcase.vue"),
        name: "IonicShowcase",
        meta: uiKitMeta,
      },
      {
        path: "layouts-showcase",
        component: () =>
          import(/* webpackChunkName: "LayoutsShowcase" */ "@/views/uiKit/LayoutsShowcase.vue"),
        name: "LayoutsShowcase",
        meta: uiKitMeta,
      },
      {
        path: "skeletons-showcase",
        component: () =>
          import(/* webpackChunkName: "SkeletonsShowcase" */ "@/views/uiKit/SkeletonsShowcase.vue"),
        name: "SkeletonsShowcase",
        meta: uiKitMeta,
      },
      {
        path: "states-showcase",
        component: () =>
          import(/* webpackChunkName: "StatesShowcase" */ "@/views/uiKit/StatesShowcase.vue"),
        name: "StatesShowcase",
        meta: uiKitMeta,
      },
      {
        path: "utilities-showcase",
        component: () =>
          import(/* webpackChunkName: "UtilitiesShowcase" */ "@/views/uiKit/UtilitiesShowcase.vue"),
        name: "UtilitiesShowcase",
        meta: uiKitMeta,
      },
      {
        path: "widgets-showcase",
        component: () =>
          import(/* webpackChunkName: "WidgetsShowcase" */ "@/views/uiKit/WidgetsShowcase.vue"),
        name: "WidgetsShowcase",
        meta: uiKitMeta,
      },
      {
        path: "cards-showcase",
        component: () =>
          import(/* webpackChunkName: "CardsShowcase" */ "@/views/uiKit/CardsShowcase.vue"),
        name: "CardsShowcase",
        meta: uiKitMeta,
      },
    ],
  },

  // ── Tabs demo (learning sandbox) ─────────────────────────────────────────────────────────────
  // A dummy bottom-tab shell mirroring the real StudentTabsShell pattern. The parent renders
  // DemoTabsShell (an <ion-tabs>), whose nested <ion-router-outlet> swaps in the children below —
  // each tab keeps its own stack. Open /ui-kit/tabs-demo (no login needed). Throwaway demo.
  // Kept OUTSIDE the UiShowcase hub: an <ion-tabs> shell hosts its own router outlet, and stacking
  // it inside UiShowcase's nested outlet is the double-outlet transition Ionic crashes on.
  {
    path: "/ui-kit/tabs-demo",
    component: () =>
      import(/* webpackChunkName: "DemoTabsShell" */ "@/views/uiKit/DemoTabsShell.vue"),
    meta: {
      requiresAuth: false,
      allowedRoleIds: AllUserRoleIds,
      hideFooterNavigator: true,
    },
    children: [
      { path: "", redirect: "/ui-kit/tabs-demo/tab1" },
      {
        path: "tab1",
        component: () =>
          import(/* webpackChunkName: "DemoTab1" */ "@/components/uiKit/tabsDemo/DemoTab1.vue"),
        name: "DemoTab1",
        meta: { scrollToTop: true },
      },
      {
        path: "tab2",
        component: () =>
          import(/* webpackChunkName: "DemoTab2" */ "@/components/uiKit/tabsDemo/DemoTab2.vue"),
        name: "DemoTab2",
        meta: { scrollToTop: true },
      },
      {
        path: "tab3",
        component: () =>
          import(/* webpackChunkName: "DemoTab3" */ "@/components/uiKit/tabsDemo/DemoTab3.vue"),
        name: "DemoTab3",
        meta: { scrollToTop: true },
      },
      // FLAT sibling child (URL /tab1-detail) — its path is NOT under the tab1 segment, so Ionic
      // does not treat it as Tab 1's location: switch tabs and return → you land on the tab root.
      {
        path: "tab1-detail",
        component: () =>
          import(
            /* webpackChunkName: "DemoTab1Detail" */ "@/components/uiKit/tabsDemo/DemoTab1Detail.vue"
          ),
        name: "DemoTab1Detail",
        meta: { scrollToTop: true },
      },
      // NESTED child (URL /tab1/nested-detail) — path lives under the tab1 segment, so Ionic tracks
      // it as Tab 1's current location and RESTORES it when you switch tabs and come back.
      {
        path: "tab1/nested-detail",
        component: () =>
          import(
            /* webpackChunkName: "DemoTab1NestedDetail" */ "@/components/uiKit/tabsDemo/DemoTab1NestedDetail.vue"
          ),
        name: "DemoTab1NestedDetail",
        meta: { scrollToTop: true },
      },
      // Full-screen page kept as a CHILD of the shell with the tab bar hidden (meta.hideTabBar).
      // It pushes onto the nested outlet (ion-page ↔ ion-page) and pops back cleanly — avoiding the
      // Ionic crash that occurs when a top-level outlet transitions back into the <ion-tabs> view.
      // (Contrast: the real student app makes conduct-* routes top-level siblings, which hits that
      // crash on return — see notes.)
      {
        path: "immersive",
        component: () =>
          import(
            /* webpackChunkName: "DemoFullScreen" */ "@/components/uiKit/tabsDemo/DemoFullScreen.vue"
          ),
        name: "DemoFullScreen",
        meta: { scrollToTop: true, hideTabBar: true },
      },
    ],
  },
] as unknown as Array<RouteRecordRaw>;
